/**
 * Audio Processing Service for OpenAI Realtime API
 *
 * Handles browser microphone access, audio streaming, and playback
 * Optimized for medical environments with noise suppression
 */

class AudioProcessor {
  constructor() {
    this.audioContext = null;
    this.microphone = null;
    this.audioWorkletNode = null;
    this.mediaStream = null;
    this.isRecording = false;
    this.isPlaying = false;
    this.sampleRate = 24000; // OpenAI Realtime API requirement (24kHz)
    this.channelCount = 1; // Mono audio
    this.bufferSize = 4096;

    // Voice Activity Detection state
    this.vadThreshold = 0.01;
    this.vadLevel = 0;
    this.vadSmoothingFactor = 0.8;
    this.isSpeaking = false;
    this.speechStartTime = null;
    this.speechEndTime = null;
    this.silenceFrames = 0;
    this.speechFrames = 0;
    this.minSpeechFrames = 5; // Minimum frames to consider as speech
    this.minSilenceFrames = 10; // Minimum silence frames to end speech

    // Audio processing settings optimized for medical environments
    this.audioConstraints = {
      audio: {
        sampleRate: { ideal: this.sampleRate, min: 16000, max: 48000 },
        channelCount: this.channelCount,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        googEchoCancellation: true,
        googAutoGainControl: true,
        googNoiseSuppression: true,
        googHighpassFilter: true,
        googTypingNoiseDetection: true,
        googAudioMirroring: false,
      },
    };

    // Audio processing callbacks
    this.onAudioData = null;
    this.onVoiceActivity = null;
    this.onError = null;

    // Voice activity detection
    this.vadThreshold = 0.01;
    this.vadSmoothingFactor = 0.8;
    this.vadLevel = 0;
    this.isSpeaking = false;
    this.silenceTimeout = null;
    this.silenceDuration = 500; // ms
  }

  /**
   * Check if audio recording is supported in this browser/environment
   */
  static async checkAudioSupport() {
    const support = {
      mediaDevices: !!navigator.mediaDevices,
      getUserMedia: !!(
        navigator.mediaDevices && navigator.mediaDevices.getUserMedia
      ),
      webAudio: !!(window.AudioContext || window.webkitAudioContext),
      audioWorklet: !!(
        window.AudioContext && AudioContext.prototype.audioWorklet
      ),
      secureContext: window.isSecureContext,
      protocol: location.protocol,
      permissions: "permissions" in navigator,
      microphone: null,
    };

    // Check microphone permission if possible
    if (support.permissions) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "microphone",
        });
        support.microphone = permissionStatus.state;
      } catch (error) {
        support.microphone = "unknown";
      }
    }

    return support;
  }

  /**
   * Initialize audio context and check browser support
   */
  async initialize() {
    try {
      // Check if we're running in a secure context (HTTPS or localhost)
      if (
        !window.isSecureContext &&
        location.protocol !== "https:" &&
        location.hostname !== "localhost"
      ) {
        throw new Error("Microphone access requires HTTPS or localhost");
      }

      // Check for required browser APIs
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          "Browser does not support audio recording. Please use Chrome, Firefox, or Safari."
        );
      }

      // Check for permissions
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "microphone",
        });
        console.log("Microphone permission status:", permissionStatus.state);

        if (permissionStatus.state === "denied") {
          throw new Error(
            "Microphone access denied. Please allow microphone access in your browser settings."
          );
        }
      } catch (permError) {
        console.warn("Could not check microphone permissions:", permError);
        // Continue anyway, as some browsers don't support permissions API
      }

      // Initialize Web Audio API
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) {
        throw new Error(
          "Web Audio API not supported. Please use a modern browser."
        );
      }

      // Create audio context with fallback sample rate
      try {
        this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
      } catch (contextError) {
        console.warn(
          "Failed to create audio context with preferred sample rate, using default"
        );
        this.audioContext = new AudioContext();
      }

      // Resume audio context if suspended (required for some browsers)
      if (this.audioContext.state === "suspended") {
        console.log("Resuming suspended audio context...");
        await this.audioContext.resume();
      }

      console.log("Audio processor initialized successfully");
      console.log("Sample rate:", this.audioContext.sampleRate);
      console.log("Audio context state:", this.audioContext.state);
      console.log("Secure context:", window.isSecureContext);

      return true;
    } catch (error) {
      console.error("Failed to initialize audio processor:", error);
      this.onError?.(error);
      return false;
    }
  }

  /**
   * Request microphone access and start recording
   */
  async startRecording() {
    if (this.isRecording) {
      console.log("Already recording");
      return true;
    }

    try {
      // Initialize if not already done
      if (!this.audioContext) {
        console.log("Initializing audio context...");
        const initialized = await this.initialize();
        if (!initialized) {
          throw new Error("Failed to initialize audio context");
        }
      }

      // Request microphone access with detailed error handling
      console.log("Requesting microphone access...");
      console.log("Audio constraints:", this.audioConstraints);

      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia(
          this.audioConstraints
        );
        console.log("Microphone access granted");
        console.log(
          "Media stream tracks:",
          this.mediaStream.getTracks().map((track) => ({
            kind: track.kind,
            label: track.label,
            enabled: track.enabled,
            readyState: track.readyState,
          }))
        );
      } catch (micError) {
        console.error("Microphone access error:", micError);

        // Provide specific error messages based on error type
        if (micError.name === "NotAllowedError") {
          throw new Error(
            "Microphone access denied. Please click the microphone icon in your browser address bar and allow access."
          );
        } else if (micError.name === "NotFoundError") {
          throw new Error(
            "No microphone found. Please connect a microphone and try again."
          );
        } else if (micError.name === "NotReadableError") {
          throw new Error(
            "Microphone is already in use by another application."
          );
        } else if (micError.name === "OverconstrainedError") {
          throw new Error(
            "Microphone constraints cannot be satisfied. Please try with different settings."
          );
        } else if (micError.name === "SecurityError") {
          throw new Error(
            "Microphone access blocked by browser security. Please use HTTPS or localhost."
          );
        } else {
          throw new Error(`Microphone error: ${micError.message}`);
        }
      }

      // Create audio source
      console.log("Creating audio source...");
      this.microphone = this.audioContext.createMediaStreamSource(
        this.mediaStream
      );

      // Set up audio processing worklet
      console.log("Setting up audio worklet...");
      await this.setupAudioWorklet();

      this.isRecording = true;
      console.log("Recording started successfully");
      console.log("Audio context state:", this.audioContext.state);

      return true;
    } catch (error) {
      console.error("Failed to start recording:", error);
      this.onError?.(error);

      // Cleanup on error
      await this.stopRecording();

      return false;
    }
  }

  /**
   * Stop recording and release microphone
   */
  stopRecording() {
    if (!this.isRecording) return;

    try {
      // Stop media stream
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
        this.mediaStream = null;
      }

      // Disconnect audio nodes
      if (this.audioWorkletNode) {
        this.audioWorkletNode.disconnect();
        this.audioWorkletNode = null;
      }

      if (this.microphone) {
        this.microphone.disconnect();
        this.microphone = null;
      }

      // Clear voice activity detection
      if (this.silenceTimeout) {
        clearTimeout(this.silenceTimeout);
        this.silenceTimeout = null;
      }

      this.isRecording = false;
      this.isSpeaking = false;
      this.vadLevel = 0;

      console.log("Recording stopped");
    } catch (error) {
      console.error("Error stopping recording:", error);
      this.onError?.(error);
    }
  }

  /**
   * Set up audio worklet for real-time processing
   */
  async setupAudioWorklet() {
    try {
      // Register audio worklet processor
      await this.audioContext.audioWorklet.addModule(
        "/audio-processor-worklet.js"
      );

      // Create worklet node
      this.audioWorkletNode = new AudioWorkletNode(
        this.audioContext,
        "audio-processor",
        {
          processorOptions: {
            sampleRate: this.sampleRate,
            vadThreshold: this.vadThreshold,
          },
        }
      );

      // Handle processed audio data
      this.audioWorkletNode.port.onmessage = (event) => {
        const { type, data } = event.data;

        switch (type) {
          case "audioData":
            this.handleAudioData(data);
            break;
          case "voiceActivity":
            this.handleVoiceActivity(data);
            break;
          case "error":
            console.error("Audio worklet error:", data);
            this.onError?.(new Error(data));
            break;
        }
      };

      // Connect audio pipeline
      this.microphone.connect(this.audioWorkletNode);
      // Note: Worklet doesn't need to connect to destination for processing

      console.log("Audio worklet setup complete");
    } catch (error) {
      console.error("Failed to setup audio worklet:", error);
      // Fallback to script processor for older browsers
      await this.setupScriptProcessor();
    }
  }

  /**
   * Handle processed audio data from worklet
   */
  handleAudioData(audioData) {
    console.log(
      "AudioProcessor: Received audio data, length:",
      audioData ? audioData.length : "null"
    );

    // Call the callback if it exists
    if (this.onAudioData && typeof this.onAudioData === "function") {
      this.onAudioData(audioData);
    } else {
      console.warn("AudioProcessor: onAudioData callback not set");
    }
  }

  /**
   * Handle voice activity detection data from worklet
   */
  handleVoiceActivity(vadData) {
    // Call the callback if it exists
    if (this.onVoiceActivity && typeof this.onVoiceActivity === "function") {
      this.onVoiceActivity(vadData);
    }
  }

  /**
   * Convert Float32Array to Int16Array for OpenAI API
   */
  convertToInt16(float32Array) {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      // Clamp and convert to 16-bit signed integer
      const clamped = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = Math.round(clamped * 0x7fff);
    }
    return int16Array;
  }

  /**
   * Voice activity detection for ScriptProcessor fallback
   */
  detectVoiceActivity(channelData) {
    // Calculate RMS (Root Mean Square) energy
    let rmsSum = 0;
    for (let i = 0; i < channelData.length; i++) {
      rmsSum += channelData[i] * channelData[i];
    }
    const rms = Math.sqrt(rmsSum / channelData.length);

    // Apply exponential smoothing
    this.vadLevel =
      this.vadLevel * this.vadSmoothingFactor +
      rms * (1 - this.vadSmoothingFactor);

    // Determine if current frame has speech
    const currentFrameHasSpeech = this.vadLevel > this.vadThreshold;

    // Track speech and silence frames
    if (currentFrameHasSpeech) {
      this.speechFrames++;
      this.silenceFrames = 0;
    } else {
      this.silenceFrames++;
      this.speechFrames = 0;
    }

    // Determine speech state changes
    let eventType = null;
    const wasSpeeking = this.isSpeaking;

    // Start speech detection
    if (!this.isSpeaking && this.speechFrames >= this.minSpeechFrames) {
      this.isSpeaking = true;
      this.speechStartTime = Date.now();
      eventType = "speechStart";
      console.log("VAD: Speech started, level:", this.vadLevel.toFixed(4));
    }
    // End speech detection
    else if (this.isSpeaking && this.silenceFrames >= this.minSilenceFrames) {
      this.isSpeaking = false;
      this.speechEndTime = Date.now();
      eventType = "speechEnd";
      console.log("VAD: Speech ended, level:", this.vadLevel.toFixed(4));
    }

    return {
      level: this.vadLevel,
      rms: rms,
      isSpeaking: this.isSpeaking,
      timestamp: Date.now(),
      type: eventType,
      speechDuration: this.speechStartTime
        ? Date.now() - this.speechStartTime
        : 0,
    };
  }

  /**
   * Fallback audio processing using ScriptProcessorNode (deprecated but widely supported)
   */
  async setupScriptProcessor() {
    console.log("Using ScriptProcessor fallback for audio processing");

    try {
      // Create script processor node
      this.audioWorkletNode = this.audioContext.createScriptProcessor(
        this.bufferSize,
        1,
        1
      );

      // Process audio data
      this.audioWorkletNode.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer;
        const channelData = inputBuffer.getChannelData(0);

        // Convert to Int16Array for OpenAI Realtime API
        const audioData = this.convertToInt16(channelData);

        // Voice activity detection
        const vadResult = this.detectVoiceActivity(channelData);

        // Send processed data
        this.handleAudioData(audioData);
        this.handleVoiceActivity(vadResult);
      };

      // Connect audio pipeline
      this.microphone.connect(this.audioWorkletNode);
      this.audioWorkletNode.connect(this.audioContext.destination);

      console.log("ScriptProcessor audio processing setup complete");
    } catch (error) {
      console.error("Failed to setup script processor:", error);
      throw error;
    }
  }

  /**
   * Handle processed audio data
   */
  handleAudioData(audioData) {
    if (this.onAudioData && this.isRecording) {
      // Only send audio data if we're actively recording
      this.onAudioData(audioData);
    }
  }

  /**
   * Handle voice activity detection results
   */
  handleVoiceActivity(vadData) {
    const { level, isSpeaking } = vadData;

    this.vadLevel = level;

    // Update speaking state
    if (isSpeaking && !this.isSpeaking) {
      this.isSpeaking = true;
      this.onVoiceActivity?.({ type: "speechStart", level });

      // Clear any pending silence timeout
      if (this.silenceTimeout) {
        clearTimeout(this.silenceTimeout);
        this.silenceTimeout = null;
      }
    } else if (!isSpeaking && this.isSpeaking) {
      // Don't immediately stop - wait for silence duration
      if (this.silenceTimeout) {
        clearTimeout(this.silenceTimeout);
      }

      this.silenceTimeout = setTimeout(() => {
        this.isSpeaking = false;
        this.onVoiceActivity?.({ type: "speechEnd", level });
        this.silenceTimeout = null;
      }, this.silenceDuration);
    }

    // Always send level updates for visualization
    this.onVoiceActivity?.({
      type: "levelUpdate",
      level,
      isSpeaking: this.isSpeaking,
    });
  }

  /**
   * Simple voice activity detection for ScriptProcessor fallback
   */
  detectVoiceActivity(channelData) {
    // Calculate RMS (Root Mean Square) level
    let sum = 0;
    for (let i = 0; i < channelData.length; i++) {
      sum += channelData[i] * channelData[i];
    }
    const rms = Math.sqrt(sum / channelData.length);

    // Apply smoothing
    this.vadLevel =
      this.vadLevel * this.vadSmoothingFactor +
      rms * (1 - this.vadSmoothingFactor);

    // Determine if speaking
    const isSpeaking = this.vadLevel > this.vadThreshold;

    return { level: this.vadLevel, isSpeaking };
  }

  /**
   * Convert Float32Array to Int16Array for OpenAI Realtime API
   */
  convertToInt16(float32Array) {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      // Clamp values to [-1, 1] and convert to 16-bit signed integer
      const clamped = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = clamped * 0x7fff;
    }
    return int16Array;
  }

  /**
   * Play audio response from OpenAI
   */
  async playAudio(audioData) {
    if (this.isPlaying) {
      console.warn("Already playing audio");
      return;
    }

    try {
      if (!this.audioContext) {
        await this.initialize();
      }

      this.isPlaying = true;

      // Convert Int16Array back to Float32Array
      const float32Data = this.convertToFloat32(audioData);

      // Create audio buffer
      const audioBuffer = this.audioContext.createBuffer(
        1,
        float32Data.length,
        this.sampleRate
      );
      audioBuffer.copyToChannel(float32Data, 0);

      // Create and configure buffer source
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;

      // Add some gain control
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 0.8; // Slightly reduce volume

      // Connect audio pipeline
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Handle playback completion
      source.onended = () => {
        this.isPlaying = false;
        console.log("Audio playback completed");
      };

      // Start playback
      source.start();

      console.log("Started audio playback");
    } catch (error) {
      console.error("Failed to play audio:", error);
      this.isPlaying = false;
      this.onError?.(error);
    }
  }

  /**
   * Convert Int16Array to Float32Array for playback
   */
  convertToFloat32(int16Array) {
    const float32Array = new Float32Array(int16Array.length);
    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 0x7fff;
    }
    return float32Array;
  }

  /**
   * Get current audio levels for visualization
   */
  getAudioLevels() {
    return {
      vadLevel: this.vadLevel,
      isSpeaking: this.isSpeaking,
      isRecording: this.isRecording,
      isPlaying: this.isPlaying,
    };
  }

  /**
   * Resample audio data to target sample rate
   */
  resampleAudio(audioData, fromRate, toRate) {
    if (fromRate === toRate) {
      return audioData;
    }

    const ratio = toRate / fromRate;
    const newLength = Math.round(audioData.length * ratio);
    const resampled = new Int16Array(newLength);

    // Simple linear interpolation resampling
    for (let i = 0; i < newLength; i++) {
      const srcIndex = i / ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const srcIndexCeil = Math.min(srcIndexFloor + 1, audioData.length - 1);
      const fraction = srcIndex - srcIndexFloor;

      const sample1 = audioData[srcIndexFloor] || 0;
      const sample2 = audioData[srcIndexCeil] || 0;

      resampled[i] = Math.round(sample1 + (sample2 - sample1) * fraction);
    }

    console.log(
      `🔄 Resampled audio: ${fromRate}Hz → ${toRate}Hz (${audioData.length} → ${newLength} samples)`
    );
    return resampled;
  }

  /**
   * Play audio data through speakers
   */
  async playAudio(audioData) {
    try {
      if (!this.audioContext) {
        await this.initialize();
      }

      // Ensure audio context is running
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      console.log("🔊 Playing audio data, length:", audioData.length);

      // Ensure we're working with the correct sample rate for playback
      const playbackSampleRate = this.audioContext.sampleRate;
      const openaiSampleRate = 24000;

      // Resample if needed (OpenAI sends 24kHz, but device might be different)
      let processedAudio = audioData;
      if (playbackSampleRate !== openaiSampleRate) {
        console.log(
          `🔄 Resampling for playback: ${openaiSampleRate}Hz → ${playbackSampleRate}Hz`
        );
        processedAudio = this.resampleAudio(
          audioData,
          openaiSampleRate,
          playbackSampleRate
        );
      }

      // Convert Int16Array to Float32Array for Web Audio API
      const float32Array = new Float32Array(processedAudio.length);
      for (let i = 0; i < processedAudio.length; i++) {
        float32Array[i] = processedAudio[i] / 0x7fff; // Convert from 16-bit to float
      }

      // Create audio buffer with device's native sample rate
      const audioBuffer = this.audioContext.createBuffer(
        1, // mono
        float32Array.length,
        playbackSampleRate // Use device's native sample rate
      );

      // Copy data to buffer
      audioBuffer.getChannelData(0).set(float32Array);

      // Create and connect audio source
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);

      // Play audio
      this.isPlaying = true;
      source.start(0);

      // Handle playback end
      source.onended = () => {
        this.isPlaying = false;
        console.log("✅ Audio playback completed");
      };

      console.log("🎵 Audio playback started");
      return true;
    } catch (error) {
      console.error("❌ Failed to play audio:", error);
      this.isPlaying = false;
      throw error;
    }
  }

  /**
   * Update voice activity detection settings
   */
  updateVADSettings(settings) {
    if (settings.threshold !== undefined) {
      this.vadThreshold = settings.threshold;
    }
    if (settings.silenceDuration !== undefined) {
      this.silenceDuration = settings.silenceDuration;
    }
    if (settings.smoothingFactor !== undefined) {
      this.vadSmoothingFactor = settings.smoothingFactor;
    }

    // Update worklet processor if available
    if (this.audioWorkletNode && this.audioWorkletNode.port) {
      this.audioWorkletNode.port.postMessage({
        type: "updateSettings",
        settings: {
          vadThreshold: this.vadThreshold,
        },
      });
    }
  }

  /**
   * Check browser audio capabilities
   */
  static async checkAudioSupport() {
    const support = {
      mediaDevices: !!navigator.mediaDevices,
      getUserMedia: !!(
        navigator.mediaDevices && navigator.mediaDevices.getUserMedia
      ),
      audioContext: !!(window.AudioContext || window.webkitAudioContext),
      audioWorklet: false,
      scriptProcessor: false,
    };

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const tempContext = new AudioContext();

      // Check for AudioWorklet support
      if (tempContext.audioWorklet) {
        support.audioWorklet = true;
      }

      // Check for ScriptProcessor support
      if (tempContext.createScriptProcessor) {
        support.scriptProcessor = true;
      }

      tempContext.close();
    } catch (error) {
      console.warn("Error checking audio support:", error);
    }

    return support;
  }

  /**
   * Cleanup resources
   */
  destroy() {
    this.stopRecording();

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close();
    }

    this.audioContext = null;
    this.onAudioData = null;
    this.onVoiceActivity = null;
    this.onError = null;
  }
}

// Create singleton instance
const audioProcessor = new AudioProcessor();

export { audioProcessor, AudioProcessor };
